import './style.css';

import { bind } from 'bind-decorator';
import { Button, Field, Report } from 'bz-core-controls';
import DesktopBase from 'bz-core-controls/controls/desktop-base';
import { IconButton } from 'bz-core-controls/controls/next/menu';
import PageTab from 'bz-core-controls/controls/page-header/controls/page-tab';
import Icon from 'bz-icons/src/controls/icon';
import JsonServiceClient from 'bz-stores/lib/JsonServiceClient';
import { css as cssType, js } from 'bz-utilities/files/types';
import history from 'bz-utilities/modules/networking/history/rr4';
import { action, computed, observable, runInAction, toJS } from 'mobx';
import { inject, observer } from 'mobx-react';
import QueryString from 'query-string';
import React from 'react';
import { useLocation } from 'react-router-dom';

import SlideDialog from 'tm3-core/controls/modal/slide-dialog';

import type ReportsStore from '../../stores/reports-store';

class BackBtn extends React.Component<any> {
  render() {
    if (!window.location.pathname.startsWith('/reports/bespoke/view')) return null;

    return (
      <IconButton className="back-button" onClick={this.onClick}>
        <Icon className="white" value="0001_back_left_arrow" />
      </IconButton>
    );
  }

  private onClick() {
    history.replace(`/reports`);
  }
}
interface IProps {
  reportsStore?: ReportsStore;
}

import useComputed from 'bz-utilities/hooks/computed';
import { addMonths, endOfQuarter, startOfDay, startOfQuarter } from 'date-fns';
import { ClinicReportsSaveRequest } from 'tm3-core/api.dtos';
import { getDateRange } from './getDateRange';

@inject('reportsStore')
@observer
class ReportView extends React.Component<IProps> {
  @computed
  get reportName() {
    const path = this.reportPath;
    let res = path.substring(path.lastIndexOf('/') + 1);
    if (path) {
      return res.substring(0, res.indexOf('.repx'));
    } else {
      return '';
    }
  }
  @computed
  get reportPath() {
    const { report } = this;
    if (report) {
      return report?.url;
    }
    return '';
  }
  @computed
  get report() {
    let { path, reportId } = QueryString.parse(window.location.search);
    const url = encodeURI(path);
    const reportIdGuid = (reportId ?? '').toLowerCase();
    if (path) return this.props.reportsStore?.reports.find((x) => x.url?.endsWith(url));
    if (reportId) {
      const report = this.props.reportsStore?.reports.find(
        (x) => x.id.toLowerCase() === reportIdGuid
      );
      if (report) return report;
    }
    return null;
  }
  @observable showSaveDialog = false;
  @observable saveName = '';
  @observable saveDescription = '';

  @observable canSave = false;
  @observable userSelectedParams: any = {};

  @action.bound
  onParamsChanged(params: any) {
    this.canSave = !!this.report;
    this.userSelectedParams = params;
  }

  @action.bound
  async onSave() {
    const client = new JsonServiceClient();
    const { id: reportId } = this.report!;
    var params: any = { hideParams: false };
    for (let k in this.userSelectedParams) {
      const p = toJS(this.userSelectedParams[k]);
      if (p.type == 'System.DateTime' && p.value?.length === 2) {
        params[k] = getDateRange(p.value);
      } else {
        params[k] = JSON.stringify(p.value);
      }
    }
    const url = `bespoke/view?reportId=${reportId}` + '&' + QueryString.stringify(params);
    await client.post(
      new ClinicReportsSaveRequest({
        reportId,
        name: this.saveName || this.reportName,
        description: this.saveDescription,
        data: url
      })
    );
    runInAction(() => {
      this.showSaveDialog = false;
    });
  }

  render() {
    let { reportName } = this;
    let { reportPath } = this;
    let { path, reportId, ...other } = QueryString.parse(window.location.search);
    for (let k in other) {
      if (other[k] === null) delete other[k]; // devexpress doesn't like these
    }
    const content = (
      <DxReportViewer path={reportPath} params={other} onParamsChanged={this.onParamsChanged} />
    );

    if (process.env.isClick) return content;
    return (
      <DesktopBase
        title={'Reports > ' + decodeURIComponent(reportName)}
        backBtn={<BackBtn />}
        headerContent={
          this.canSave && (
            <PageTab
              label={'Save'}
              style={{ cursor: 'pointer' }}
              onClick={() => {
                this.showSaveDialog = true;
              }}
            />
          )
        }
        className="bespoke"
      >
        <div className="grid h-full grid-rows-1">{content}</div>
        <SlideDialog
          open={this.showSaveDialog}
          onClose={() => (this.showSaveDialog = false)}
          header={
            <>
              Save Preset
              <Button hovered={true} label="Save" onClick={this.onSave} primary={true} />
            </>
          }
        >
          <div className="p-4">
            <Field
              maxLength={50}
              value={this.saveName}
              label="Preset Name"
              onChange={(e) => (this.saveName = e.target.value)}
            />
            <Field
              maxLength={200}
              value={this.saveDescription}
              label="Preset Description"
              onChange={(e) => (this.saveDescription = e.target.value)}
            />
          </div>
        </SlideDialog>
      </DesktopBase>
    );
  }
}

const NewUxReportViewWrapper = () => {
  return <ReportView />;
};

interface IDxReportViewerProps {
  path: string;
  params?: any;
  onParamsChanged?: (params: any) => void;
}

export class DxReportViewer extends React.Component<IDxReportViewerProps> {
  bindings: any;
  viewer: any;
  runOnMount: boolean = false;
  parameterCapture: any = {};
  constructor(props: IDxReportViewerProps) {
    super(props);
    this.viewer = React.createRef();
    if (!document.getElementById('doc-viewer')) {
      const css = document.createElement('link');
      css.rel = 'stylesheet';
      css.type = cssType;
      css.href = '/api/assets/document-viewer.css';
      css.id = 'doc-viewer';
      document.getElementsByTagName('head')[0].appendChild(css);
    }
    if (!document.getElementById('doc-viewer-js')) {
      const script = document.createElement('script');
      script.type = js;
      script.src = '/api/assets/document-viewer.js';
      script.id = 'doc-viewer-js';
      script.onload = () => this.runReport();
      document.getElementsByTagName('head')[0].appendChild(script);
    } else {
      this.runOnMount = true;
    }
  }

  componentDidMount() {
    if (this.runOnMount) this.runReport();
  }
  componentWillUnmount() {
    const { ko } = window;
    if (ko) ko.cleanNode(this.viewer.current);
  }

  @bind
  runReport() {
    if (!this.viewer.current) {
      this.runOnMount = true;
      return null;
    }

    if (this.bindings) return null;
    const { path, params, onParamsChanged } = this.props;
    const reportDef = JSON.stringify({ path, params });

    const { ko } = window;
    const invokeAction = 'DXXRDV',
      host = '/api/',
      reportUrl = ko.observable(reportDef); // this is passed to the DX Report resolver
    var viewModel = {
      viewerOptions: {
        reportUrl: reportUrl,
        callbacks: {
          ParametersSubmitted: (sender, { Parameters = [] }) => {
            for (let p of Parameters) {
              this.parameterCapture[p.Key].value = p.Value;
            }
            if (onParamsChanged) onParamsChanged(this.parameterCapture);
          },
          BeforeRender: () => {
            const { predefinedDateRanges } = DevExpress.Reporting.Viewer.Widgets;
            const previousQuarter = predefinedDateRanges.find(
              (x: any) => x.displayName === 'Previous Quarter'
            );
            const now = startOfDay(new Date());

            if (previousQuarter) {
              const idx = predefinedDateRanges.indexOf(previousQuarter);
              predefinedDateRanges.splice(idx, 1, {
                displayName: 'Last Quarter',
                range: () => [
                  startOfQuarter(addMonths(now, -3)),
                  startOfDay(endOfQuarter(addMonths(now, -3)))
                ]
              });
            }
            const currentQuarter = predefinedDateRanges.find(
              (x: any) => x.displayName === 'Current Quarter'
            );
            if (currentQuarter) {
              const idx = predefinedDateRanges.indexOf(currentQuarter);
              predefinedDateRanges.splice(idx, 1, {
                displayName: 'Current Quarter',
                range: () => [startOfQuarter(now), startOfDay(endOfQuarter(now))]
              });
            }
          },
          CustomizeParameterEditors: (sender, { parameter }) => {
            this.parameterCapture[parameter.name] = {
              description: parameter.description,
              type: parameter.type,
              value: null
            };
          }
        },
        requestOptions: {
          host,
          invokeAction
        }
      }
    };
    this.bindings = ko.applyBindings(viewModel, this.viewer.current);
  }

  render() {
    const other = { 'data-bind': 'dxReportViewer: viewerOptions' };

    return (
      <div ref={this.viewer}>
        <div {...other}></div>
      </div>
    );
  }
}

export const OldReportView = observer(() => {
  const { search } = useLocation();

  const query = useComputed(() => {
    return Object.fromEntries(new URLSearchParams(search).entries());
  }, [search]);

  const reportName = useComputed(() => {
    let path = query.path ?? '';

    path = path.replace(/\\|\//g, '/');
    let res = path.substring(path.lastIndexOf('/') + 1);

    if (path) {
      return res.substring(0, res.indexOf('.repx'));
    } else {
      return '';
    }
  }, [query]);

  const title = useComputed(() => {
    return `Reports > ${decodeURIComponent(reportName)}`;
  }, [reportName]);

  const { path, ...other } = query;

  return (
    <DesktopBase title={title} backBtn={<BackBtn />} className="bespoke">
      <div className="grid h-screen">{<DxReportViewer path={path} params={other} />}</div>
    </DesktopBase>
  );
});

export default NewUxReportViewWrapper;
